import React from 'react';
import { Check } from 'lucide-react';
import { Link } from 'react-router-dom';
import { ServicePageLayout } from '../components/ServicePageLayout';
export function HeadlightRestorationPage() {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Headlight Restoration',
    provider: {
      '@type': 'LocalBusiness',
      name: "Mike's Pro Detail",
      telephone: '+18183819905'
    },
    areaServed: ['Ventura County', 'Camarillo', 'Ventura', 'Thousand Oaks'],
    description: 'Professional headlight restoration service to remove yellowing, oxidation, and fog, improving nighttime visibility and vehicle appearance.'
  };
  const faqs = [{
    question: 'How long does headlight restoration last?',
    answer: 'Because we apply a durable UV sealant as the final step, our headlight restoration lasts far longer than cheap DIY kits — typically a year or more, depending on sun exposure and care.'
  }, {
    question: 'Why do headlights turn yellow and cloudy?',
    answer: 'Headlight lenses are made of polycarbonate plastic with a factory UV clear coat. Over time that coating breaks down from sun and heat, causing the plastic underneath to oxidize and turn yellow or foggy.'
  }, {
    question: 'Is headlight restoration worth it vs. replacement?',
    answer: 'Yes. Replacing headlight assemblies can cost hundreds or thousands of dollars. Restoration delivers clear, like-new lenses for a fraction of the cost.'
  }, {
    question: 'Does it improve nighttime visibility?',
    answer: 'Significantly. Clear lenses let your bulbs project light properly, improving your vision at night and in poor weather and keeping you safer on the road.'
  }];
  const breadcrumbs = [{
    name: 'Home',
    item: 'https://www.fulldetailpro.com/'
  }, {
    name: 'Services',
    item: 'https://www.fulldetailpro.com/services'
  }, {
    name: 'Headlight Restoration',
    item: 'https://www.fulldetailpro.com/services/headlight-restoration'
  }];
  return <ServicePageLayout title="Headlight Restoration Ventura County" subtitle="Improve your nighttime visibility and enhance your car's appearance by permanently removing yellowing, oxidation, and fog from your headlights." metaDescription="Professional headlight restoration in Ventura County \u2014 Camarillo, Ventura & Thousand Oaks. Remove yellowing & oxidation for clear headlights. Call (818) 381-9905." schema={schema} faqs={faqs} breadcrumbs={breadcrumbs} data-id="element-869" data-mp="2134hfvq33mt">
      <p data-id="element-870" data-mp="3fdmxy96kaul2">
        Are your vehicle's headlights looking cloudy, yellowed, or foggy? Not
        only does this age the appearance of your car significantly, but it also
        poses a serious safety hazard by drastically reducing your nighttime
        visibility. At Mike's Pro Detail, our professional{' '}
        <strong data-id="element-871" data-mp="2wozvzzjomlbp">Headlight Restoration</strong> service permanently removes heavy
        oxidation and restores crystal-clear clarity to your lenses. We provide
        this essential mobile service throughout Camarillo CA, Thousand Oaks,
        and the greater Ventura County area.
      </p>

      <h2 data-id="element-872" data-mp="1iqm3biipethd">Why Do Headlights Turn Yellow?</h2>
      <p data-id="element-873" data-mp="1esem6mkbvq5n">
        Modern vehicle headlights are made of polycarbonate plastic, which is
        lightweight and impact-resistant. However, this plastic is highly
        susceptible to UV degradation from the sun. Manufacturers apply a
        UV-protective clear coat at the factory, but over time, this coating
        breaks down due to sun exposure, heat from the bulbs, and harsh cleaning
        chemicals. Once the coating fails, the plastic oxidizes rapidly,
        resulting in that cloudy, yellow appearance.
      </p>

      <h2 data-id="element-874" data-mp="r20qmm2jb96b">Our Professional Restoration Process</h2>
      <p data-id="element-875" data-mp="m37n9mgyoyjv">
        Don't be fooled by cheap DIY kits or "wipe-on" fixes that only last a
        few weeks. We perform a true, multi-step mechanical restoration to
        permanently fix the issue:
      </p>

      <ul className="space-y-4 my-6 list-none pl-0" data-id="element-876" data-mp="c48e82sab0wc">
        <li className="flex items-start" data-id="element-877" data-mp="c6ule1b9jee1">
          <Check className="w-6 h-6 text-red-500 mr-3 shrink-0 mt-1" data-id="element-878" data-mp="2c6hpdtqyh9gv" />
          <span data-id="element-879" data-mp="2h5ujmzk5pith">
            <strong data-id="element-880" data-mp="2h6ds7pnx2uuu">Taping & Protection:</strong> We carefully tape off the
            surrounding paint and trim to ensure no damage occurs during the
            sanding process.
          </span>
        </li>
        <li className="flex items-start" data-id="element-881" data-mp="27f9eet9z9g1l">
          <Check className="w-6 h-6 text-red-500 mr-3 shrink-0 mt-1" data-id="element-882" data-mp="27dyb0ukg2ab0" />
          <span data-id="element-883" data-mp="1tfgsdb9ebnzy">
            <strong data-id="element-884" data-mp="1teylzlt0rteb">Wet Sanding:</strong> We use progressive grits of
            specialized sandpaper to completely strip away the failed factory
            clear coat and the oxidized plastic layer.
          </span>
        </li>
        <li className="flex items-start" data-id="element-885" data-mp="1oh6weyfukjda">
          <Check className="w-6 h-6 text-red-500 mr-3 shrink-0 mt-1" data-id="element-886" data-mp="1oi86p0p1h1ty" />
          <span data-id="element-887" data-mp="3dwd3hrvgc7h4">
            <strong data-id="element-888" data-mp="3dvv4wolbhh61">Machine Compounding:</strong> Using a machine polisher and
            heavy cutting compound, we remove the fine sanding marks and begin
            restoring clarity.
          </span>
        </li>
        <li className="flex items-start" data-id="element-889" data-mp="178mi7urhm265">
          <Check className="w-6 h-6 text-red-500 mr-3 shrink-0 mt-1" data-id="element-890" data-mp="175i7sfnfia71" />
          <span data-id="element-891" data-mp="12akxl5eexusk">
            <strong data-id="element-892" data-mp="126x2w2x8f2c9">Machine Polishing:</strong> A refining polish is used to
            bring the polycarbonate lens back to a flawless, glass-like
            transparency.
          </span>
        </li>
        <li className="flex items-start" data-id="element-893" data-mp="1c54hwg7ynabm">
          <Check className="w-6 h-6 text-red-500 mr-3 shrink-0 mt-1" data-id="element-894" data-mp="1pxkots3zfsht" />
          <span data-id="element-895" data-mp="1lwqyks32i1lr">
            <strong data-id="element-896" data-mp="1lxab1t9y7xse">UV Protection Application:</strong> This is the most
            critical step. We apply a durable specialized UV
            sealant to protect the bare plastic and prevent it from yellowing
            again.
          </span>
        </li>
      </ul>

      <h2 data-id="element-897" data-mp="1uvm4e32799oj">Safety and Aesthetics</h2>
      <p data-id="element-898" data-mp="5hphqw0iyv8v">
        Restoring your headlights is one of the most cost-effective ways to
        instantly improve the look of your vehicle. More importantly, clear
        headlights allow your bulbs to project light properly, significantly
        improving your vision at night and during inclement weather, keeping you
        and your family safe on the road.
      </p>

      <h2 data-id="element-899" data-mp="29mbz1chuv3qp">Convenient Mobile Service</h2>
      <p data-id="element-900" data-mp="29lkb8nui2aaz">
        Replacing headlight assemblies can cost hundreds or even thousands of
        dollars. Our restoration service is a fraction of the cost and yields
        incredible results. If you are searching for "Mobile Detailing Ventura
        County" or "Car Detailing Camarillo" to fix your cloudy headlights,
        Mike's Pro Detail will come directly to your home or workplace to
        perform the restoration.
      </p>

      <h2 data-id="element-901" data-mp="2tcp0kk02h0xv">Related Detailing Services</h2>
      <div className="not-prose grid grid-cols-1 sm:grid-cols-2 gap-3 my-6" data-id="element-902" data-mp="2xc66fejeo03k">
        <Link to="/full-detail" className="block px-4 py-3 bg-zinc-900/60 border border-zinc-800 rounded-lg text-white font-bold hover:border-red-900/60 hover:bg-zinc-800/60 transition-all" data-id="element-903" data-mp="32b9df4iy4ge6">
          Full Car Detail &rarr;
        </Link>
        <Link to="/services/exterior-detailing" className="block px-4 py-3 bg-zinc-900/60 border border-zinc-800 rounded-lg text-white font-bold hover:border-red-900/60 hover:bg-zinc-800/60 transition-all" data-id="element-904" data-mp="3heu0wk3pbn67">
          Exterior Detailing &rarr;
        </Link>
        <Link to="/services/engine-bay-detailing" className="block px-4 py-3 bg-zinc-900/60 border border-zinc-800 rounded-lg text-white font-bold hover:border-red-900/60 hover:bg-zinc-800/60 transition-all" data-id="element-905" data-mp="33ej8hcmqu0ix">
          Engine Bay Detailing &rarr;
        </Link>
        <Link to="/services/mobile-detailing" className="block px-4 py-3 bg-zinc-900/60 border border-zinc-800 rounded-lg text-white font-bold hover:border-red-900/60 hover:bg-zinc-800/60 transition-all" data-id="element-906" data-mp="3v67hbp6bc4x0">
          Mobile Detailing &rarr;
        </Link>
      </div>
    </ServicePageLayout>;
}